import { SaveAsCompositeMode } from '@project/components/project/project-save/save-project.service';
import { PropertyValueVO, PropertyVO } from '@project/components/article/model/vo/property.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { assertExhaustiveSwitch } from '@shared/util/ts-utils';
import { NumberUtils } from '@shared/util/number-utils';

export type ComplexArticleBlueprintError = { message: string };

export type ParsedArticleNumberSchema = { articleNo: string, articlesAmount: number, buildingBlockName?: string };

export type RuleExecutor = (params: ComplexArticleRuleParams, blueprint: ComplexArticleBlueprintDTO, parsedArticleNumberSchema: ParsedArticleNumberSchema) => Promise<ComplexArticleBlueprintError[] | void>

export type ComplexArticleRuleParamTypeName = 'STRING' | 'INT' | 'FLOAT' | 'BOOLEAN';

export class ComplexArticleRuleParams {
  constructor(
    private readonly properties: PropertyVO[],
  ) {
  }

  public getValue(name: string, type: 'string'): string;
  public getValue(name: string, type: 'number'): number;
  public getValue(name: string, type: 'string' | 'number') {
    if (type === 'string') {
      const value = PropertyUtils.getStringPropertyValue(this.properties, name);
      if (value != null) return value;
      throw new Error(`Expected param ${name} of type string to be given.`);
    }
    const value = PropertyUtils.getNumberPropertyValue(this.properties, name, Number.NaN);
    if (!NumberUtils.isNotNumber(value)) return value;
    throw new Error(`Expected param ${name} of type number to be given.`);
  }

  public getValues(name: string, type: 'string'): string[];
  public getValues(name: string, type: 'number'): number[];
  public getValues(name: string, type: 'string' | 'number') {
    if (type === 'string') {
      const values = PropertyUtils.getStringPropertyValues(this.properties, name);
      if (values.length > 0) return values;
      throw new Error(`Expected param ${name} of type string-array to be given.`);
    }
    const values = PropertyUtils.getIntPropertyValues(this.properties, name);
    if (values.length > 0) return values;
    throw new Error(`Expected param ${name} of type number-array to be given.`);
  }
}

type ComplexArticleRuleParamDescription = {
  type: ComplexArticleRuleParamTypeName,
  isArray?: boolean,
  required: boolean,
  default?: string | number | boolean | string[] | number[] | boolean[],
  min?: number,
  max?: number,
  regex?: string,
}

type ComplexArticleRuleParamsSchema = Record<string, ComplexArticleRuleParamDescription>;

export class ComplexArticleRule {

  public constructor(
    public readonly name: string,
    public readonly paramsSchema: ComplexArticleRuleParamsSchema,
    private readonly executor: RuleExecutor,
  ) {
  }

  public createUsage(): ComplexArticleRuleUsageVO {
    const params: PropertyVO[] = [];

    for (const [key, def] of Object.entries(this.paramsSchema)) {
      if (def.isArray) {
        // initialize with empty array — no PropertyVO needed until values are added
      } else if (Object.hasOwn(def, 'default')) {
        switch (def.type) {
          case 'STRING':
            PropertyUtils.setStringPropertyValue(params, key, String(def.default));
            break;
          case 'INT':
          case 'BOOLEAN':
            PropertyUtils.setIntPropertyValue(params, key, Number(def.default));
            break;
          case 'FLOAT':
            PropertyUtils.setFloatPropertyValue(params, key, Number(def.default));
            break;
          default:
            assertExhaustiveSwitch(def.type);
        }
      } else {
        switch (def.type) {
          case 'STRING':
            PropertyUtils.setStringPropertyValue(params, key, '');
            break;
          case 'INT':
            PropertyUtils.setIntPropertyValue(params, key, 0);
            break;
          case 'FLOAT':
            PropertyUtils.setFloatPropertyValue(params, key, 0);
            break;
          case 'BOOLEAN':
            PropertyUtils.setBooleanPropertyValue(params, key, false);
            break;
          default:
            assertExhaustiveSwitch(def.type);
        }
      }
    }

    return {
      name: this.name,
      params,
    }
  }

  public async execute(
    paramValues: PropertyVO[],
    usage: ComplexArticleBlueprintDTO,
    parsedArticleNumberSchema: ParsedArticleNumberSchema,
  ): Promise<ComplexArticleBlueprintError[]> {
    const params = new ComplexArticleRuleParams(paramValues);
    const errors = await this.executor(params, usage, parsedArticleNumberSchema);
    return Array.isArray(errors) ? errors : [];
  }

  /** validates the schema and returns a list of error messages */
  public validateRuleParams(paramValues: PropertyVO[]): string[] {

    const errors: string[] = [];

    for (const [paramName, schema] of Object.entries(this.paramsSchema)) {

      const values = PropertyUtils.getPropertyByName(paramValues, paramName)?.values ?? [];

      if (schema.isArray) {
        if (schema.required && values.length === 0) {
          errors.push(`Expected param ${paramName} of type ${schema.type}-array to be given.`);
        }
        errors.push(...values.flatMap((value) => this.validateSingleParamValue(value, paramName, schema)));
        continue;
      }

      const firstValue = values?.[0];
      const hasValue = firstValue != null && (firstValue.stringValue !== '' || firstValue.stringValue == null);

      if (schema.required && !hasValue) {
        errors.push(`Expected param ${paramName} of type ${schema.type} to be given.`);
      }

      if (!firstValue) continue;
      errors.push(...this.validateSingleParamValue(firstValue, paramName, schema));
    }
    return errors;
  }

  private validateSingleParamValue(pv: PropertyValueVO, paramName: string, schema: ComplexArticleRuleParamDescription): string[] {
    const errors: string[] = [];
    const expectToBe = `Expected param '${paramName}' to be`;

    const typeMap: Record<ComplexArticleRuleParamTypeName, string> = {
      INT: 'INT',
      FLOAT: 'FLOAT',
      STRING: 'STRING',
      BOOLEAN: 'INT',
    };

    const expectedPvType = typeMap[schema.type];
    if (pv.type !== expectedPvType) {
      errors.push(`${expectToBe} of PropertyValue type ${expectedPvType} but got ${pv.type}.`);
      return errors;
    }

    if (schema.type === 'INT' || schema.type === 'FLOAT') {
      const numVal = schema.type === 'INT' ? pv.intValue : pv.floatValue;
      if (schema.min !== undefined && numVal < schema.min) {
        errors.push(`${expectToBe} greater than ${schema.min}`);
      }
      if (schema.max !== undefined && numVal > schema.max) {
        errors.push(`${expectToBe} less than ${schema.max}`);
      }
    }
    return errors;
  }
}

export type ComplexArticleBlueprintOptionsVO = {
  isIndividualPartsModifiable: boolean,
  isIndividualPartsSelectable: boolean,
  isIndividualPartsStickTogether: boolean,
  isInitialRotationRequired: boolean,
}

export type ComplexArticleRuleUsageVO = {
  name: string,
  params: PropertyVO[],
}

export type ArticleNumberSchema = {
  articleNumber: string,
  from: number,
  to: number,
  additional: number[],
  excluded: number[],
}

export type ComplexArticleBlueprintDTO = {
  id?: number,
  name: string,
  description: string,
  articleTypeId: number,
  projectGroupId: number,
  articleNumberSchema: ArticleNumberSchema,
  saveMode: SaveAsCompositeMode,
  buildingBlockName?: string,
  rules: ComplexArticleRuleUsageVO[],
  flags: ComplexArticleBlueprintOptionsVO,
}

export type ComplexArticleBlueprintVersionOverwriteMode = 'new-version' | 'overwrite-latest-version' | 'only-new-articles';

export type ComplexArticleBlueprintRunMode = {
  internal: boolean,
  notSearchable: boolean,
  versionOverwriteMode: ComplexArticleBlueprintVersionOverwriteMode
}
